import React, { useEffect, useState } from 'react';
import {
    Box,
    Button,
    CardContent,
    Checkbox,
    FormControlLabel,
    Grid,
    MenuItem,
    TextField,
    Typography,
    Alert,
    Stack
} from '@mui/material';
import { IconBuilding, IconDeviceFloppy } from '@tabler/icons';

import { useHistory, useLocation } from 'react-router-dom';
import api from '../../../../services/api';
import PageHeader from '../../../../../ui-component/extended/PageHeader';
import GlassCard from '../../../../../ui-component/extended/GlassCard';

const CreateOrganization = () => {
    const history = useHistory();
    const location = useLocation();
    const isSupplierMode = new URLSearchParams(location.search).get('type') === 'supplier';

    const [appId, setAppId] = useState(null);
    const [status, setStatus] = useState({ type: '', message: '' });
    const [saving, setSaving] = useState(false);

    const [financialCategories, setFinancialCategories] = useState([]);
    const [costCenters, setCostCenters] = useState([]);
    const [paymentMethods, setPaymentMethods] = useState([]);
    const [paymentTerms, setPaymentTerms] = useState([]);
    const [countries, setCountries] = useState([]);

    const [formData, setFormData] = useState({
        name: '',
        description: '',
        cnpj: '',
        email: '',
        phone: '',
        address: '',
        is_customer: !isSupplierMode,
        is_supplier: true,
        person_type: 'pj',
        country: '',
        default_financial_category: '',
        default_cost_center: '',
        default_payment_method: '',
        default_payment_term: '',
        state_registration: '',
        state_registration_exempt: false,
        municipal_registration: '',
        default_invoice_type: '',
        withholds_iss: false,
        withholds_federal_taxes: false
    });

    const pegar_app_padrao = async () => {
        try {
            const response = await api.get('v1/apps/default/');
            const data = response.data;

            if (data?.app?.id) {
                setAppId(data.app.id);
            } else {
                setStatus({ type: 'error', message: 'App padrão não encontrado' });
            }
        } catch (error) {
            console.error(error);
            setStatus({ type: 'error', message: 'Erro ao buscar app padrão' });
        }
    };

    useEffect(() => {
        if (!localStorage.getItem('access_token')) return;
        pegar_app_padrao();
    }, []);

    useEffect(() => {
        if (!appId) return;
        (async () => {
            try {
                const [fc, cc, pm, pt, co] = await Promise.all([
                    api.get(`v1/apps/${appId}/financial-categories/?type=expense`),
                    api.get(`v1/apps/${appId}/cost-centers/`),
                    api.get(`v1/apps/${appId}/payment-methods/`),
                    api.get(`v1/apps/${appId}/payment-terms/`),
                    api.get(`v1/apps/${appId}/countries/`)
                ]);
                setFinancialCategories(fc.data || []);
                setCostCenters(cc.data || []);
                setPaymentMethods(pm.data || []);
                setPaymentTerms(pt.data || []);
                setCountries(co.data || []);
            } catch (error) {
                console.error(error);
            }
        })();
    }, [appId]);

    const selectedCountry = countries.find((c) => c.id === formData.country);
    // Sem país selecionado ainda, assume Brasil (comportamento de sempre,
    // não muda nada pra quem nunca mexeu nesse campo novo).
    const isForeign = !!selectedCountry && selectedCountry.code !== 'BR';

    const backPage = () => {
        history.push(isSupplierMode ? '/dashboard/config/suppliers' : '/dashboard/config/organizations/view');
    };

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleCheckboxChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.checked
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!appId) {
            setStatus({ type: 'error', message: 'App não carregado ainda' });
            return;
        }

        setStatus({ type: '', message: '' });
        setSaving(true);

        try {
            const payload = {
                ...formData,
                country: formData.country || null,
                default_financial_category: formData.default_financial_category || null,
                default_cost_center: formData.default_cost_center || null,
                default_payment_method: formData.default_payment_method || null,
                default_payment_term: formData.default_payment_term || null,
                default_invoice_type: formData.default_invoice_type || null
            };
            const response = await api.post(`v1/apps/${appId}/organizations/`, payload);

            if (response.data) {
                history.push(
                    isSupplierMode
                        ? `/dashboard/config/organizations/edit/${response.data.organization_id}?type=supplier`
                        : `/dashboard/config/organizations/edit/${response.data.organization_id}`
                );
            }
        } catch (error) {
            console.error(error);

            setStatus({
                type: 'error',
                message: error.response?.data?.detail || error.response?.data?.error || 'Erro ao criar empresa.'
            });
            setSaving(false);
        }
    };

    return (
        <Box>
            <PageHeader
                icon={IconBuilding}
                gradient="blue"
                title={isSupplierMode ? 'Adicionar fornecedor' : 'Adicionar empresa'}
                subtitle={isSupplierMode ? 'Cadastre um novo fornecedor' : 'Cadastre uma nova empresa'}
            />

            <GlassCard sx={{ mb: 3 }}>
                <CardContent>
                    {status.message && (
                        <Alert severity={status.type} sx={{ mb: 2 }}>
                            {status.message}
                        </Alert>
                    )}

                    <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <TextField name="name" label="Nome Fantasia" fullWidth value={formData.name} onChange={handleChange} required />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    select
                                    label="País"
                                    fullWidth
                                    value={formData.country}
                                    onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                                    helperText="Deixe em branco pra Brasil"
                                >
                                    <MenuItem value="">Brasil</MenuItem>
                                    {countries.map((c) => (
                                        <MenuItem key={c.id} value={c.id}>
                                            {c.name}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    name="cnpj"
                                    label={isForeign ? 'Tax ID / EIN' : 'CNPJ/CPF'}
                                    placeholder={isForeign ? 'Número de identificação fiscal no país de origem' : ''}
                                    fullWidth
                                    value={formData.cnpj}
                                    onChange={handleChange}
                                    required
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    name="person_type"
                                    select
                                    label="Tipo de Pessoa"
                                    fullWidth
                                    value={formData.person_type}
                                    onChange={handleChange}
                                >
                                    <MenuItem value="pj">Pessoa Jurídica</MenuItem>
                                    <MenuItem value="pf">Pessoa Física</MenuItem>
                                </TextField>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField name="phone" label="Telefone" fullWidth value={formData.phone} onChange={handleChange} />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField name="email" label="Email" type="email" fullWidth value={formData.email} onChange={handleChange} />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField name="address" label="Endereço" fullWidth value={formData.address} onChange={handleChange} />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    name="description"
                                    label="Descrição"
                                    fullWidth
                                    value={formData.description}
                                    onChange={handleChange}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <Stack direction="row" spacing={3}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox name="is_customer" checked={!!formData.is_customer} onChange={handleCheckboxChange} />
                                        }
                                        label="É cliente"
                                    />
                                    <FormControlLabel
                                        control={
                                            <Checkbox name="is_supplier" checked={!!formData.is_supplier} onChange={handleCheckboxChange} />
                                        }
                                        label="É fornecedor"
                                    />
                                </Stack>
                            </Grid>
                    </Grid>
                </CardContent>
            </GlassCard>

            <GlassCard>
                <CardContent>
                    <Typography variant="h6" fontWeight={700} mb={2}>
                        Classificação Financeira e Fiscal
                    </Typography>

                    <form onSubmit={handleSubmit}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    select
                                    label="Categoria Financeira padrão"
                                    fullWidth
                                    value={formData.default_financial_category}
                                    onChange={(e) => setFormData({ ...formData, default_financial_category: e.target.value })}
                                    helperText="Usada pra classificar as contas a pagar geradas pra esse cadastro"
                                >
                                    <MenuItem value="">Nenhuma</MenuItem>
                                    {financialCategories.map((c) => (
                                        <MenuItem key={c.id} value={c.id}>
                                            {c.name}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    select
                                    label="Centro de Custo padrão"
                                    fullWidth
                                    value={formData.default_cost_center}
                                    onChange={(e) => setFormData({ ...formData, default_cost_center: e.target.value })}
                                >
                                    <MenuItem value="">Nenhum</MenuItem>
                                    {costCenters.map((c) => (
                                        <MenuItem key={c.id} value={c.id}>
                                            {c.name}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    select
                                    label="Forma de Pagamento padrão"
                                    fullWidth
                                    value={formData.default_payment_method}
                                    onChange={(e) => setFormData({ ...formData, default_payment_method: e.target.value })}
                                >
                                    <MenuItem value="">Nenhuma</MenuItem>
                                    {paymentMethods.map((m) => (
                                        <MenuItem key={m.id} value={m.id}>
                                            {m.name}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    select
                                    label="Condição de Pagamento padrão"
                                    fullWidth
                                    value={formData.default_payment_term}
                                    onChange={(e) => setFormData({ ...formData, default_payment_term: e.target.value })}
                                >
                                    <MenuItem value="">Nenhuma</MenuItem>
                                    {paymentTerms.map((t) => (
                                        <MenuItem key={t.id} value={t.id}>
                                            {t.name}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>

                            {!isForeign && (
                                <>
                                    <Grid item xs={12} sm={4}>
                                        <TextField
                                            name="state_registration"
                                            label="Inscrição Estadual"
                                            fullWidth
                                            disabled={formData.state_registration_exempt}
                                            value={formData.state_registration}
                                            onChange={handleChange}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={2} display="flex" alignItems="center">
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    name="state_registration_exempt"
                                                    checked={!!formData.state_registration_exempt}
                                                    onChange={handleCheckboxChange}
                                                />
                                            }
                                            label="Isento de IE"
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            name="municipal_registration"
                                            label="Inscrição Municipal"
                                            fullWidth
                                            value={formData.municipal_registration}
                                            onChange={handleChange}
                                        />
                                    </Grid>
                                </>
                            )}

                            <Grid item xs={12} sm={6}>
                                <TextField
                                    name="default_invoice_type"
                                    select
                                    label="Tipo de Nota Fiscal padrão"
                                    fullWidth
                                    value={formData.default_invoice_type}
                                    onChange={handleChange}
                                >
                                    <MenuItem value="">Não definido</MenuItem>
                                    <MenuItem value="nfse">NFS-e (Nota Fiscal de Serviço Eletrônica)</MenuItem>
                                    <MenuItem value="invoice">Fatura/Invoice (contratação internacional)</MenuItem>
                                </TextField>
                            </Grid>

                            {!isForeign && (
                                <Grid item xs={12} sm={6}>
                                    <Stack direction="row" spacing={3} alignItems="center" height="100%">
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    name="withholds_iss"
                                                    checked={!!formData.withholds_iss}
                                                    onChange={handleCheckboxChange}
                                                />
                                            }
                                            label="Retém ISS na fonte"
                                        />
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    name="withholds_federal_taxes"
                                                    checked={!!formData.withholds_federal_taxes}
                                                    onChange={handleCheckboxChange}
                                                />
                                            }
                                            label="Retém PIS/COFINS/CSLL/IRRF na fonte"
                                        />
                                    </Stack>
                                </Grid>
                            )}

                            <Grid item xs={12}>
                                <Stack direction="row" justifyContent="flex-end" spacing={2}>
                                    <Button onClick={backPage} disabled={saving}>
                                        Cancelar
                                    </Button>
                                    <Button type="submit" variant="contained" startIcon={<IconDeviceFloppy size={18} />} disabled={saving}>
                                        {saving ? 'Salvando...' : isSupplierMode ? 'Criar fornecedor' : 'Criar empresa'}
                                    </Button>
                                </Stack>
                            </Grid>
                        </Grid>
                    </form>
                </CardContent>
            </GlassCard>
        </Box>
    );
};

export default CreateOrganization;
